import { CloudUpload } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { isArrayEmpty } from "helpers/helpers";
import { ChangeEvent, useRef, useState } from "react";
import { convertFileToBase64 } from "../Inputs";
import "./FileInput.css";
import { isValidFileSize } from "./FileInput.helpers";
import { FileInputProps, FileInputType } from "./FileInput.types";

export function FileInput({
  label,
  height = 100,
  required = false,
  disabled = false,
  acceptedFileTypes = "*",
  allowedFileTypes,
  maxSizeInMb = 2,
  errorMessage,
  onAddFile,
  t,
}: FileInputProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const isError = !!errorMessage || !!localError;

  const onChangePicture = async (e: ChangeEvent<HTMLInputElement>) => {
    setLocalError(null);
    const files = Array.from(e.target.files || []);
    if (isArrayEmpty(files)) {
      return;
    }

    const file = files[0];

    if (allowedFileTypes?.length && !allowedFileTypes.includes(file.type)) {
      setLocalError(
        t("file_type_invalid", { types: allowedFileTypes.join(", ") }),
      );
      return;
    }

    if (!isValidFileSize(file, maxSizeInMb)) {
      setLocalError(t("file_size_max_limit", { max: maxSizeInMb }));
      return;
    }

    const base64File = await convertFileToBase64(file);
    const validFile: FileInputType = {
      name: file.name,
      mimeType: file.type,
      base64File,
      url: URL.createObjectURL(file),
    };

    onAddFile(validFile);
    fileInputRef.current!.value = "";
  };

  return (
    <Stack
      className="file-input-container"
      onClick={() => !disabled && fileInputRef.current?.click()}
      justifyContent={"center"}
      alignItems={"center"}
      sx={{
        cursor: disabled ? "not-allowed" : "pointer",
        borderColor: disabled ? "divider" : isError ? "error.main" : "divider",
        height: height,
        "&:hover": {
          borderColor: disabled ? "divider" : "primary.main",
        },
      }}
    >
      {label && (
        <Typography
          variant="body2"
          color={disabled ? "text.disabled" : "text.primary"}
        >
          {t(label)}
          {required && " *"}
        </Typography>
      )}
      <CloudUpload color={disabled ? "disabled" : "primary"} fontSize="large" />
      {isError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          {errorMessage || localError}
        </Typography>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        style={{ display: "none" }}
        onChange={onChangePicture}
        disabled={disabled}
      />
    </Stack>
  );
}
